import { useCallback, useEffect, useRef } from "react";

import { stripHTML } from "../Utils/htmlStringUtils";
import { countWords } from "../Utils/countWords";
import { MIN_WORD_COUNT } from "./useSimilarAnswersFetch";

const DEBOUNCE_MS = 1000;
// Minimum additional words since the last query before a new one is fired, preventing
// spurious re-queries as the user types one word at a time mid-sentence.
const MIN_WORD_DELTA = 3;

// Manages the debounced input pipeline: strips HTML from wysiwyg output, tracks word
// count deltas to suppress redundant queries, and exposes the current raw value so
// callers can fire manual re-fetches (reset / seek-more) outside the debounce cycle.
//
// `fetchSimilarAnswers` must be a stable (text: string) => void callback. The caller
// is responsible for pre-binding any additional arguments (e.g. ignoredAnswerIds).
//
// `enabled` disables the handler entirely when suggestions are dismissed or reviewed,
// avoiding unnecessary debounce work after the user has stopped interacting.
export const useDebouncedAnswerQuery = (fetchSimilarAnswers, { enabled }) => {
  const currentValueRef = useRef("");
  const debounceTimerRef = useRef(null);
  // Tracks the word count of the most recent fired query so the delta check below can
  // suppress near-identical re-queries. Reset to 0 after a seek/reset cycle so the
  // next typing event re-queries without waiting for MIN_WORD_DELTA new words.
  const lastQueriedWordCountRef = useRef(0);

  // Exposed so useSimilarAnswers can reset delta tracking before manual re-fetches
  // (seek-more / reset-review), ensuring those fetches are not blocked by the delta
  // guard.
  const resetLastQueryCount = useCallback(() => {
    lastQueriedWordCountRef.current = 0;
  }, []);

  const handleValueChange = useCallback(
    value => {
      if (!enabled) return;

      // The wysiwyg editor emits HTML tags that would inflate word count and corrupt the
      // query.
      const strippedValue = stripHTML(value || "");
      currentValueRef.current = strippedValue;

      const wordCount = countWords(strippedValue);

      clearTimeout(debounceTimerRef.current);

      if (wordCount < MIN_WORD_COUNT) {
        lastQueriedWordCountRef.current = 0;
        // Delegate the clear to fetchSimilarAnswers rather than reaching into its state:
        // it handles the below-threshold case by clearing results immediately.
        fetchSimilarAnswers(strippedValue);
        return;
      }

      debounceTimerRef.current = setTimeout(() => {
        const delta = wordCount - lastQueriedWordCountRef.current;
        if (lastQueriedWordCountRef.current > 0 && delta < MIN_WORD_DELTA) return;

        lastQueriedWordCountRef.current = wordCount;
        fetchSimilarAnswers(strippedValue);
      }, DEBOUNCE_MS);
    },
    [enabled, fetchSimilarAnswers]
  );

  useEffect(() => {
    return () => {
      clearTimeout(debounceTimerRef.current);
    };
  }, []);

  return { handleValueChange, currentValueRef, resetLastQueryCount };
};
